// src/components/bans/AddSuspensionModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { SuspensionType } from '../../types/suspension';
import { useSuspensionStore } from '../../stores/suspensionStore';
import { useHotelsStore } from '../../stores/hotelsStore'; // Add this import
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddSuspensionModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [selectedType, setSelectedType] = useState<SuspensionType>('linking');
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState<'temporary' | 'permanent'>('temporary');
  const [expiryDate, setExpiryDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { createSuspension } = useSuspensionStore();
  const { hotels, fetchHotels } = useHotelsStore(); // Add this line

  // Add useEffect to fetch hotels
  useEffect(() => {
    if (isOpen) {
      fetchHotels();
    }
  }, [isOpen, fetchHotels]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason || (selectedType === 'specific_hotel' && !selectedHotelId)) {
      toast.error(translations?.addSuspensionModal?.errors?.allFieldsRequired || 'All fields are required');
      return;
    }

    if (duration === 'temporary' && !expiryDate) {
      toast.error(translations?.addSuspensionModal?.errors?.expiryDateRequired || 'Please select an expiry date');
      return;
    }

    setIsLoading(true);
    try {
      const suspensionData = {
        type: selectedType,
        reason,
        createdAt: Date.now(),
        expiresAt: duration === 'permanent' ? null : new Date(expiryDate).getTime(),
        createdBy: localStorage.getItem('userId') || '',
        createdByName: localStorage.getItem('userName') || '',
        isActive: true
      };

      if (selectedType === 'specific_hotel') {
        const hotel = hotels[selectedHotelId];
        Object.assign(suspensionData, {
          hotelId: selectedHotelId,
          hotelName: hotel.name
        });
      }

      await createSuspension(suspensionData);
      toast.success(translations?.addSuspensionModal?.success?.suspensionAdded || 'Suspension added successfully');
      onClose();
    } catch (error) {
      toast.error(translations?.addSuspensionModal?.errors?.suspensionAddFailed || 'Failed to add suspension');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.addSuspensionModal?.title || 'Add New Suspension'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Suspension Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addSuspensionModal?.labels?.suspensionType || 'Suspension Type'}
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as SuspensionType)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="linking">{translations?.addSuspensionModal?.suspensionTypes?.linking || 'Linking Service Suspension'}</option>
            <option value="all_accommodation">{translations?.addSuspensionModal?.suspensionTypes?.allAccommodation || 'All Accommodation Suspension'}</option>
            <option value="specific_hotel">{translations?.addSuspensionModal?.suspensionTypes?.specificHotel || 'Specific Hotel Suspension'}</option>
            <option value="camp_accommodation">{translations?.addSuspensionModal?.suspensionTypes?.campAccommodation || 'Camp Accommodation Suspension'}</option>
          </select>
        </div>

        {/* Hotel Selection (for specific_hotel type) */}
        {selectedType === 'specific_hotel' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.addSuspensionModal?.labels?.hotel || 'Hotel'}
            </label>
            <select
              value={selectedHotelId}
              onChange={(e) => setSelectedHotelId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">{translations?.addSuspensionModal?.labels?.selectHotel || 'Select Hotel'}</option>
              {Object.values(hotels).map((hotel) => (
                <option key={hotel.id} value={hotel.id}>{hotel.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addSuspensionModal?.labels?.reason || 'Reason'}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            rows={3}
            required
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations?.addSuspensionModal?.labels?.duration || 'Duration'}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={duration === 'temporary'}
                onChange={() => setDuration('temporary')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addSuspensionModal?.labels?.temporary || 'Temporary'}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={duration === 'permanent'}
                onChange={() => setDuration('permanent')}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span>{translations?.addSuspensionModal?.labels?.permanent || 'Permanent'}</span>
            </label>
          </div>
        </div>

        {/* Expiry Date */}
        {duration === 'temporary' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.addSuspensionModal?.labels?.expiryDate || 'Expiry Date'}
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.addSuspensionModal?.buttons?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg text-white transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {isLoading
              ? translations?.addSuspensionModal?.buttons?.adding || 'Adding...'
              : translations?.addSuspensionModal?.buttons?.addSuspension || 'Add Suspension'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
